import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as Lucide from "lucide-react";
import useHttp from "../../../hooks/use-http";

export const iconMap = {
  Home: "Home",
  Administrator: "FileText",
  "Procurement Console": "ShoppingBag",
  "Sales Console": "TreePalm",
  "Commercial  Console": "Ellipsis",
  "Warehouse Console": "Filter",
  "Warehouse Operation": "Warehouse",
  "Finance Console": "Book",
  "System Admin": "UserCog",
  "Agri Promoter": "Podcast",
  Nursery: "Sprout",
};

export default function SidebarComponent() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("div-collapsed") === "true"
  );
  // const [hovered, setHovered] = useState(false); // Removed hovered state
  const [menusData, setMenusData] = useState([]);
  const url = localStorage.getItem("organizationIcon");
  const caseType = localStorage.getItem("useCase");
  const { sendRequest: getServiceData } = useHttp();

  const serviceMenuData = () => {
    getServiceData(
      {
        url: `/api/menuServiceRoleAllocation/?userId=${localStorage.getItem(
          "id"
        )}`,
      },
      (data) => {
        const menuData = data.output;
        localStorage.setItem("userMenu", JSON.stringify(menuData));
        setMenusData(menuData);
      }
    );
  };

  useEffect(() => {
    serviceMenuData();
  }, [getServiceData]);

  const isMenuActive = (menu) => {
    if (menu.menuUrl && location.pathname === menu.menuUrl) return true;
    if (menu.menuItemList) {
      return menu.menuItemList.some((child) => isMenuActive(child));
    }
    if (menu.subItemList) {
      return menu.subItemList.some((child) => isMenuActive(child));
    }
    return false;
  };

  function MenuTree({ menu, depth = 0 }) {
    const [open, setOpen] = useState(false);
    const active = isMenuActive(menu);
    const itemRef = useRef(null);
    const isSidebarCollapsed = collapsed; // Use local variable for clarity

    useEffect(() => {
      if (active && itemRef.current) {
        itemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [active]);

    useEffect(() => {
      if (active) setOpen(true);
    }, [active]);

    if (menu.menuItemList) {
      const Icon =
        Lucide[iconMap[menu.title] || menu.iconClass || "CircleChevronRight"] ||
        Lucide.CircleChevronRight;

      return (
        <div
          className="sidebar-section"
          ref={itemRef}
          // Removed onMouseEnter={() => collapsed && setOpen(true)}
          // Removed onMouseLeave={() => collapsed && setOpen(false)}
        >
          <div
            className={`menu-item has-children${active ? " active" : ""}`}
            tabIndex={0}
            onClick={() => !isSidebarCollapsed && setOpen((o) => !o)} // Only open if NOT collapsed
            style={{ paddingLeft: 8 + depth * 16 }}
          >
            <span className="icon">
              <Icon size={22} />
            </span>
            {!isSidebarCollapsed ? ( // Check only for collapsed state
              <span className="label">{menu.title}</span>
            ) : (
              <span className="tooltip">{menu.title}</span>
            )}
            <span className={`expand-icon${open ? " open" : ""}`}>
              <Lucide.ChevronDown size={18} />
            </span>
          </div>
          {/* Submenu renders only if open and NOT collapsed */}
          {open && !isSidebarCollapsed && (
            <div className="submenu">
              {menu.menuItemList?.map((item, idx) => (
                <MenuTree
                  key={item.menuTittle + idx}
                  menu={item}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    if (menu.subItemList) {
      return (
        <SubMenuGroup
          menu={menu}
          depth={depth}
          collapsed={isSidebarCollapsed}
          // Removed hovered prop
          active={active}
        />
      );
    }

    return (
      <NavLink
        to={menu.menuUrl ?? "#"}
        key={menu.menuTittle}
        className={({ isActive }) => `menu-link${isActive ? " active" : ""}`}
      >
        <div
          ref={itemRef}
          className={`menu-item${active ? " active" : ""}`}
          style={{ paddingLeft: 7 + depth * 16 }}
          tabIndex={0}
        >
          <span className="icon">
            <Lucide.Dot size={40} />
          </span>
          {!isSidebarCollapsed ? ( // Check only for collapsed state
            <span className="label">{menu.menuTittle}</span>
          ) : (
            <span className="tooltip">{menu.menuTittle}</span>
          )}
        </div>
      </NavLink>
    );
  }

  function SubMenuGroup({ menu, depth, collapsed, active }) {
    // Removed hovered prop
    const [open, setOpen] = useState(active);
    const itemRef = useRef(null);

    useEffect(() => {
      if (active && itemRef.current) {
        itemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [active]);

    useEffect(() => {
      if (active) setOpen(true);
    }, [active]);

    return (
      <div
        ref={itemRef}
        // Removed onMouseEnter={() => collapsed && setOpen(true)}
        // Removed onMouseLeave={() => collapsed && setOpen(false)}
      >
        <div
          className={`menu-item has-children${active ? " active" : ""}`}
          tabIndex={0}
          onClick={() => !collapsed && setOpen((o) => !o)} // Only open if NOT collapsed
          style={{ paddingLeft: 8 + depth * 16 }}
        >
          <span className="icon">
            <Lucide.CircleChevronRight size={20} />
          </span>
          {!collapsed ? ( // Check only for collapsed state
            <span className="label">{menu.menuTittle}</span>
          ) : (
            <span className="tooltip">{menu.menuTittle}</span>
          )}
          <span className={`expand-icon${open ? " open" : ""}`}>
            <Lucide.ChevronDown size={16} />
          </span>
        </div>
        {/* Submenu renders only if open and NOT collapsed */}
        {open && !collapsed && (
          <div className="submenu">
            {menu.subItemList.map((item, idx) => (
              <MenuTree
                key={item.menuTittle + idx}
                menu={item}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={`custom-sidebar${collapsed ? " collapsed" : ""}`} // Removed && !hovered
        // Removed onMouseEnter={() => setHovered(true)}
        // Removed onMouseLeave={() => setHovered(false)}
      >
        <div className="sidebar-header">
          {!collapsed && ( // Check only for collapsed state
            <img
              src={`${process.env.REACT_APP_FILE_URL}${url}`}
              alt="Org Logo"
              className="org-logo"
            />
          )}
          {!collapsed ? ( // Check only for collapsed state
            <div className="org-info-row">
              <div className="org-details">
                <div className="org-title">
                  {localStorage.getItem("organizationName")}
                </div>
                {/* <div className="org-usecase">
                  {localStorage.getItem("appName")}
                </div> */}
              </div>

              <button
                className="collapse-btn-inline"
                onClick={() => {
                  setCollapsed((p) => !p);
                  localStorage.setItem("div-collapsed", !collapsed);
                }}
                tabIndex={0}
              >
                <Lucide.ChevronLeft size={20} />
              </button>
            </div>
          ) : (
            <button
              className="collapse-btn"
              onClick={() => {
                setCollapsed((p) => !p);
                localStorage.setItem("div-collapsed", !collapsed);
              }}
              tabIndex={0}
            >
              <Lucide.ChevronRight size={22} />
            </button>
          )}
        </div>

        <nav className="sidebar-menu">
          <NavLink to="/" className="menu-link" key="home">
            <div
              className={`menu-item${
                location.pathname === "/" ? " active" : ""
              }`}
              tabIndex={0}
              style={{ paddingLeft: collapsed ? 6 : 9 }} // Check only for collapsed state
            >
              <span className="icon">
                <Lucide.Home size={22} />
              </span>
              {!collapsed ? ( // Check only for collapsed state
                <span className="label">Home</span>
              ) : (
                <span className="tooltip">Home</span>
              )}
            </div>
          </NavLink>
          {menusData.length > 0 &&
            menusData.map((item, idx) => (
              <MenuTree menu={item} key={item.title + idx} depth={0} />
            ))}
        </nav>

        <div className="sidebar-footer">
          <div className="footer-content">
            <img
              src="dist/img/logo.png"
              alt="Profile"
              className="profile-img"
            />
            {!collapsed ? ( // Check only for collapsed state
              <div className="user-">Powered By</div>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
        .custom-sidebar {
            width: 250px;
            background:#eaeaea;  
            color: #222;
            height: 100vh;
            display: flex;
            flex-direction: column;
            box-shadow: 2px 0 12px rgba(30, 40, 90, 0.09);
            transition: width 0.16s cubic-bezier(.4, 0, .2, 1);
            position: relative;
            overflow: hidden;
        }
        .custom-sidebar.collapsed {
            width: 72px;
        }
   .sidebar-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 10px;
  border-bottom: 1.5px solid #f1f3f6;
  background: #eaeaea;
  transition: all 0.2s ease;
}

.org-logo {

  height: 100px;

  object-fit: cover;
 
  margin-bottom: 10px;
}

.org-info-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}

.org-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
  flex: 1;
}

.org-title {
  font-size: 1rem;
  font-weight: 600;
  color: #222;
}

.org-usecase {
  font-size: 0.86rem;
  color: #666;
}

.collapse-btn-inline {
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.collapse-btn-inline:hover {
  color: #1677ff;
}

/* For collapsed state styling */
.custom-sidebar.collapsed .sidebar-header {
  flex-direction: column;
  align-items: center;
}

.collapse-btn {
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 1.2rem;
  cursor: pointer;
  margin-top: 6px;
}
        .custom-sidebar.collapsed .sidebar-header-content {
            flex-direction: column;
            gap: 8px;
            opacity: 1 !important; /* force visible */
  pointer-events: auto !important;
        }
        .sidebar-menu {
            list-style: none;
            padding: 0;
            margin: 24px 0 0 0;
            flex-grow: 1;          
            overflow-y: auto;     
            overflow-x: hidden; 
        }
        .sidebar-menu::-webkit-scrollbar {
            width: 6px;
        }
        .sidebar-menu::-webkit-scrollbar-thumb {
            background: #cfd8dc;
            border-radius: 4px;
        }
        .sidebar-menu::-webkit-scrollbar-thumb:hover {
            background: #90a4ae;
        }
        .menu-item {
            position: relative;
            padding: 5px 0px 5px 0px;
            display: flex;
            align-items: center;
            cursor: pointer;
            min-height: 38px;
            user-select: none;
            border-radius: 20px;
            transition: background 0.12s;
        }
        .has-children .expand-icon {
            margin-left: auto;
            margin-right: 5px;
            transition: transform 0.16s;
        }
        .menu-item .icon {
            font-size: 1.27rem;
            color: #61677c;
            display: inline-block;
            width: 38px;
            height: 38px;
            border-radius: 10px;
            margin-left: 8px;
            // margin-right: 9px;
            text-align: center;
            line-height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.15s;
        }
        .menu-item .label {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            vertical-align: middle;
            font-size: 0.95rem;
            font-weight: 500;
            color: #262626;
        }
        .menu-item .tooltip {
            display: none;
            position: absolute;
            left: 80px;
            top: 11px;
            background: #262626;
            color: #fff;
            padding: 3px 14px;
            border-radius: 7px;
            font-size: 0.80rem;
            white-space: nowrap;
            z-index: 10;
        }
        .custom-sidebar.collapsed .menu-item:hover .tooltip {
            display: block;
        }
        .custom-sidebar.collapsed .label {
            display: none;
        }
        .custom-sidebar.collapsed .sidebar-footer {
            justify-content: center;
        }
        .sidebar-footer {
            padding: 6px 12px;
            display: flex;
            justify-content: center; 
            border-top: 1.4px solid #f1f3f6;
            background: #eaeaea;  
            // min-height: 120px;
        }
        .footer-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
        }
        .profile-img {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            object-fit: cover;
        }
        .user-name {
            font-size: 0.9rem;
            font-weight: 600;
            text-align: center;
        }
        .user-email {
            font-size: 0.87rem;
            color: #8f8f8f;
        }
        .has-children .expand-icon {
            margin-left: auto;
            transition: transform 0.16s;
        }
        .has-children .expand-icon.open {
            transform: rotate(180deg);
        }
        .submenu {
            margin-left: 0;
        }
        .menu-item.active .icon,
        .menu-item:hover .icon {
            color: #1677ff;
        }
        .menu-item.active .label {
            color: #1677ff;
        }
        @media (max-width: 768px) {
          .custom-sidebar {
            width: 100vw;
            border-radius: 0;
          }
          .sidebar-footer,
          .sidebar-header {
            padding-left: 10px;
            padding-right: 10px;
          }
        }
        .menu-link {
          text-decoration: none;
        }
        .sidebar-menu::-webkit-scrollbar {
          width: 6px;
        }
        .sidebar-menu::-webkit-scrollbar-thumb {
          background: #cfd8dc;
          border-radius: 4px;
        }
        .sidebar-menu::-webkit-scrollbar-thumb:hover {
          background: #90a4ae;
        }
        @media (max-width: 1024px) {
          .custom-sidebar {
            width: 220px;
            min-width: 0;
            position: fixed;
            left: 0;
            top: 0;
            height: 100vh;
            z-index: 200;
            box-shadow: 2px 0 20px rgba(30,40,90,0.15);
            transition: left 0.25s;
          }
          .custom-sidebar.collapsed {
            width: 56px;
            min-width: 56px;
          }
        }
        @media (max-width: 600px) {
          .custom-sidebar {
            width: 100vw;
            min-width: 50px;
            border-radius: 0;
            left: 0;
            top: 0;
            max-width: 100vw;
            height: 100vh;
            box-shadow: none;
          }
          .sidebar-header,
          .sidebar-footer {
            padding-left: 10px;
            padding-right: 10px;
          }
          .sidebar-menu {
            margin-top: 8px;
            margin-bottom: 8px;
          }
          .sidebar-header-content {
            gap: 10px;
          }
        }
      `}</style>
    </>
  );
}
